import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from '../../../../redux/store';
import { ORDER_GUIDES } from '../../../../reusable-components/datagrid/orderGuidesColumns';
import { getManageOrderGuide, getOrderGuides } from '../../../../redux/slices/orderGuides';
import { HOST_API_URL } from '../../../../config-global';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../../reusable-components/iconify';
import NewOrderGuideDialog from './NewOrderGuideDialog';
import OrderGuideTemplate from './OrderGuideTemplate';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import ManageOrderGuides from './manage-guides/ManageOrderGuides';
import TableBulkDelete from '../../../../reusable-components/datagrid/TableBulkDelete';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';

export default function OrderGuidesView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: { orderGuideActivations, groups, facilities, categories, vendors, subcategories, allProducts } } = useSelector((state) => state.orderGuides);
  const isLoading = useSelector((state) => state.orderGuides.isLoading);
  const canEditOrderGuides = useScopeCheck(["Edit-OrderGuide"], true);

  const [openManageOrderGuides, setOpenManageOrderGuides] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [startDeleting, setStartDeleting] = useState(false);
  const [submitingDelete, setSubmitingDelete] = useState(false);

  useEffect(() => {
    dispatch(getOrderGuides());
  }, [dispatch]);

  useEffect(() => {
    if (openManageOrderGuides && (!subcategories || subcategories.length === 0) && (!vendors || vendors.length === 0)) dispatch(getManageOrderGuide());
  }, [openManageOrderGuides, subcategories, vendors]);

  const downloadGuides = async () => {
    //currently this is not working on dev environment as HOST_API_URL is not set but it will work on production
    window.open(`${HOST_API_URL}Api/DownloadOrderGuideActivations`, '_blank');
  };

  const bulkRemoveGuides = (ids) => {
    setSubmitingDelete(true);
    axiosInstance.post("DeleteOrderGuides", objectToFormData({ ids }))
      .then(() => {
        enqueueSnackbar("Order Guides Deleted", { variant: "success" });
        setSelectedForDelete([]);
        setStartDeleting(false);
        dispatch(getOrderGuides());
      })
      .catch(() => enqueueSnackbar("Order Guides Delete Failed", { variant: "error" }))
      .finally(() => setSubmitingDelete(false))
  };

  const customToolbar = useCallback(
    () => (
      <Grid container direction="row" spacing={1}>
        {canEditOrderGuides && <><Grid item>
          <NewOrderGuideDialog />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setOpenManageOrderGuides(true)}
            startIcon={<Iconify icon="mdi:gear-outline" />}
          >
            Manage Order Guides
          </Button>
        </Grid></>}
        <Grid item>
          <OrderGuideTemplate />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={downloadGuides}
            startIcon={<Iconify icon="file-icons:microsoft-excel" />}
          >
            Download
          </Button>
        </Grid>
        <Grid item>
          <TableBulkDelete setSelectedForDelete={setSelectedForDelete} setStartDelete={setStartDeleting} startDelete={startDeleting} loading={submitingDelete}
            onSubmit={() => bulkRemoveGuides(selectedForDelete)} />
        </Grid>
      </Grid>
    ),
    [startDeleting, submitingDelete, selectedForDelete]);

  return (
    <>
      <Grid item xs={12}>
        <CustomDataGrid
          gridId="admin-order-guides-view"
          boxSX={{ height: 'calc(100vh - 225px)' }}
          data={orderGuideActivations}
          gridColumns={ORDER_GUIDES}
          sort={[{ field: 'description', sort: 'asc' }]}
          CustomLeftToolbar={customToolbar}
          isLoading={isLoading}
          getRowId={(row) => row.id}
          checkboxSelection={startDeleting}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedForDelete(newSelection);
          }}
          rowSelectionModel={selectedForDelete}
          onRowClick={(params, e) => {
            const selection = window.getSelection().toString();
            if (selection.length > 0) return;
            if (
              e.target.classList.contains('action-button') ||
              e.target.dataset.field === 'actions' ||
              e.target?.nearestViewportElement?.dataset?.field === 'actions' ||
              e.target?.nearestViewportElement?.classList.contains('action-button')
            ) {
              return;
            }
            navigate(`/dashboard/admin/order-guides/${params.id}`);
          }}
        />
        <ManageOrderGuides
          open={openManageOrderGuides}
          setOpen={setOpenManageOrderGuides}
          groups={groups}
          facilities={facilities}
          categories={categories}
          vendors={vendors}
          subcategories={subcategories}
          allProducts={allProducts}
        />
      </Grid>
    </>
  );
}
